/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { PayloadAction, Draft, WritableDraft } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { merge, mergeWith } from 'lodash';
import { defaultTimeSpan, defaultTimeStep } from '@opengeoweb/time-slider';
import {
  SYNCGROUPS_TYPE_SETBBOX,
  SYNCGROUPS_TYPE_SETFEATURES,
  SYNCGROUPS_TYPE_SETTIME,
} from './constants';
import { getFeatureGroupId } from './featureLinks';
import type {
  SynchronizationGroupState,
  SyncGroupAddTargetPayload,
  SyncGroupAddGroupPayload,
  SyncGroupRemoveTargetPayload,
  SyncGroupRemoveGroupPayload,
  SyncGroupSetViewStatePayload,
  SyncGroupLinkTargetPayload,
  SyncGroupRemoveSourcePayload,
  SyncGroupsAddSourcePayload,
  SyncGroupToggleIsTimeScrollingEnabled,
  UpdateLinkedMap,
  AddFeatureSyncState,
  DeleteFeatureSyncState,
  DeleteFeatureLayer,
  SetLinkedMap,
  SynchronizationSource,
  SynchronizationGroup,
} from './types';
import { setBboxSync, setTimeSync } from '../synchronizationActions/actions';
import type {
  SetBboxSyncActionPayload,
  SetTimeSyncActionPayload,
} from '../synchronizationActions/types';
import type { SetTimePayload, SetBboxPayload } from '../types';
import { removeInPlace } from '../utils';

export const initialState: SynchronizationGroupState = {
  sources: {
    byId: {},
    allIds: [],
  },
  groups: {
    byId: {},
    allIds: [],
  },
  viewState: {
    timeslider: {
      groups: [],
      sourcesById: [],
    },
    zoompane: {
      groups: [],
      sourcesById: [],
    },
    level: {
      groups: [],
      sourcesById: [],
    },
    features: {
      groups: [],
      sourcesById: [],
    },
  },
  isTimeScrollingEnabled: false,
};

export const slice = createSlice({
  initialState,
  name: 'synchronizationGroupsReducer',
  reducers: {
    /** Sets the links map.
     *  This is used to override the links when changing workspaces.
     * @param draft sync state
     * @param {Record<string, string[]>} action.newLinks the new links object
     */
    setLinkedMap: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SetLinkedMap>,
    ) => {
      const { newLinks } = action.payload;
      const featureGroupIds = draft.groups.allIds.filter(
        (groupId) =>
          draft.groups.byId[groupId]?.type === SYNCGROUPS_TYPE_SETFEATURES,
      );

      featureGroupIds.forEach((groupId) => {
        draft.groups.byId[groupId].targets = {
          byId: {},
          allIds: [],
        };
      });
      draft.sources.allIds.forEach((sourceId) => {
        const source = draft.sources.byId[sourceId];
        if (source.types.includes(SYNCGROUPS_TYPE_SETFEATURES)) {
          source.targets = { byId: {}, allIds: [] };
        }
      });

      Object.entries(newLinks).forEach(([panelId, mapIds]) => {
        ensureFeatureGroupForPanel(draft, panelId);
        const targetIds = Array.from(new Set(mapIds));
        ensureFeatureSourceTargetsForPanel(draft, panelId);
        draft.sources.byId[panelId].targets = createTargets(targetIds);
      });
    },
    /** Updates the links map.
     *  This is used to update the links when operating the link menu.
     * @param draft sync state
     * @param {string} action.panelId id of the panel updating it's links
     * @param {string[]} action.mapIds the new linked maps
     */
    updateLinkedMap: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<UpdateLinkedMap>,
    ) => {
      const { panelId, mapIds } = action.payload;
      const groupId = ensureFeatureGroupForPanel(draft, panelId);
      const targetIds = Array.from(new Set(mapIds));
      draft.groups.byId[groupId].targets = { byId: {}, allIds: [] };
      ensureFeatureSourceTargetsForPanel(draft, panelId);
      draft.sources.byId[panelId].targets = createTargets(targetIds);
    },
    removeLinkedMap: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<string>,
    ) => {
      const panelId = action.payload;
      // Remove panelId from sources
      delete draft.sources.byId[panelId];
      removeInPlace(draft.sources.allIds, (_id) => _id === panelId);
      // Remove panelId from groups
      Object.keys(draft.groups.byId).forEach((groupId) => {
        const group = draft.groups.byId[groupId];
        delete group.targets.byId[panelId];
        removeInPlace(group.targets.allIds, (_id) => _id === panelId);
      });
      Object.values(draft.sources.byId).forEach((source) => {
        if (!source.targets) {
          return;
        }
        // eslint-disable-next-line no-param-reassign
        delete source.targets.byId[panelId];
        removeInPlace(source.targets.allIds, (_id) => _id === panelId);
      });

      const featureGroupId = getFeatureGroupId(panelId);
      if (draft.groups.byId[featureGroupId]) {
        delete draft.groups.byId[featureGroupId];
        removeInPlace(draft.groups.allIds, (_id) => _id === featureGroupId);
      }
    },
    addFeatureSyncState: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<AddFeatureSyncState>,
    ) => {
      const { panelId, data } = action.payload;

      if (!draft.sources.byId[panelId]) {
        draft.sources.byId[panelId] = {
          types: [SYNCGROUPS_TYPE_SETFEATURES],
          payloadByType: {},
        };
        if (!draft.sources.allIds.includes(panelId)) {
          draft.sources.allIds.push(panelId);
        }
      }

      if (
        !draft.sources.byId[panelId].types.includes(SYNCGROUPS_TYPE_SETFEATURES)
      ) {
        draft.sources.byId[panelId].types.push(SYNCGROUPS_TYPE_SETFEATURES);
      }
      if (!draft.sources.byId[panelId].targets) {
        draft.sources.byId[panelId].targets = {
          byId: {},
          allIds: [],
        };
      }

      if (!Object.keys(data).length) {
        return;
      }

      const existing = draft.sources.byId[panelId].featureSyncState;
      draft.sources.byId[panelId].featureSyncState = {
        ...existing,
        ...data,
      };
    },
    deleteFeatureSyncState: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<DeleteFeatureSyncState>,
    ) => {
      const { panelId } = action.payload;

      if (draft.sources.byId[panelId]) {
        delete draft.sources.byId[panelId].featureSyncState;
      }
    },
    deleteFeatureLayer: (
      draft: Draft<SynchronizationGroupState>,
      _action: PayloadAction<DeleteFeatureLayer>,
    ) => {
      // Feature-sync layers are now written directly to the layers store.
    },
    syncGroupAddSource: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupsAddSourcePayload>,
    ) => {
      const { id, type: payloadType, defaultPayload } = action.payload;

      if (!draft.sources.byId[id]) {
        draft.sources.byId[id] = {
          types: payloadType,
          payloadByType: {
            SYNCGROUPS_TYPE_SETTIME: defaultPayload!,
            SYNCGROUPS_TYPE_SETBBOX: defaultPayload!,
            SYNCGROUPS_TYPE_SETFEATURES: defaultPayload!,
            SYNCGROUPS_TYPE_SETLAYERACTIONS: defaultPayload!,
          },
        };

        draft.sources.allIds.push(id);
      } else {
        const existingSource = draft.sources.byId[id];
        if (
          payloadType.includes(SYNCGROUPS_TYPE_SETBBOX) &&
          payloadType.includes(SYNCGROUPS_TYPE_SETTIME) &&
          !payloadType.includes(SYNCGROUPS_TYPE_SETFEATURES) &&
          !existingSource.featureSyncState
        ) {
          existingSource.types = payloadType;
          delete existingSource.targets;
        }
        payloadType.forEach((type) => {
          if (!existingSource.types.includes(type)) {
            existingSource.types.push(type);
          }
          if (!(type in existingSource.payloadByType)) {
            existingSource.payloadByType[type] = defaultPayload!;
          }
        });
      }

      if (payloadType.includes(SYNCGROUPS_TYPE_SETFEATURES)) {
        ensureFeatureSourceTargetsForPanel(draft, id);
        const featureGroup = draft.groups.byId[getFeatureGroupId(id)];
        if (featureGroup?.type === SYNCGROUPS_TYPE_SETFEATURES) {
          const existingTargets = draft.sources.byId[id].targets;
          if (!existingTargets?.allIds.length) {
            draft.sources.byId[id].targets = {
              allIds: [...featureGroup.targets.allIds],
              byId: { ...featureGroup.targets.byId },
            };
          }
        }
      }

      /* Now try to initialize with the correct values from the state */
      draft.groups.allIds.forEach((groupId) => {
        const syncronizationGroup = draft.groups.byId[groupId];
        /* Check if the source is in the target list of the synchonizationGroup */
        const source = syncronizationGroup.targets.byId[id];

        const { type } = syncronizationGroup;
        /* If the source is part of the target list, and is linked, continue syncing the other targets */
        if (source?.linked) {
          draft.sources.byId[id].payloadByType[type] =
            syncronizationGroup.payloadByType?.[type] || defaultPayload;
        }
      });
    },

    syncGroupRemoveSource: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupRemoveSourcePayload>,
    ) => {
      const { id } = action.payload;
      if (draft.sources.byId[id]) {
        delete draft.sources.byId[id];
        removeInPlace(draft.sources.allIds, (_id) => _id === id);
      }
    },
    syncGroupAddTarget: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupAddTargetPayload>,
    ) => {
      const { groupId, targetId, linked } = action.payload;
      const synchronizationGroup = draft.groups.byId[groupId];

      if (!synchronizationGroup) {
        console.warn(`SYNCGROUPS_ADD_TARGET: Group ${groupId} does not exist.`);
        return;
      }
      if (!targetId) {
        console.warn(`SYNCGROUPS_ADD_TARGET: targetId is not defined.`);
        return;
      }
      if (synchronizationGroup.targets.allIds.includes(targetId)) {
        return;
      }
      const sourcePanelId = getFeatureSourcePanelId(synchronizationGroup);
      if (
        synchronizationGroup.type === SYNCGROUPS_TYPE_SETFEATURES &&
        sourcePanelId
      ) {
        ensureFeatureSourceTargetsForPanel(draft, sourcePanelId);
        if (
          !draft.sources.byId[sourcePanelId].targets!.allIds.includes(targetId)
        ) {
          draft.sources.byId[sourcePanelId].targets!.allIds.push(targetId);
        }
        draft.sources.byId[sourcePanelId].targets!.byId[targetId] = {
          linked: linked !== false,
        };
        return;
      }

      synchronizationGroup.targets.allIds.push(targetId);
      synchronizationGroup.targets.byId[targetId] = {
        linked: linked !== false,
      };
      const isSourceLinked =
        synchronizationGroup.targets.byId[targetId]?.linked;

      if (isSourceLinked) {
        // In order to make syncgroup work correctly we need to have defaults
        const defaultS = {
          payloadByType: {
            SYNCGROUPS_TYPE_SETTIME: {
              value: {
                timeSliderSpan: defaultTimeSpan,
                timeSliderStep: defaultTimeStep,
              },
            },
          },
        };
        if (draft.sources.byId[targetId]) {
          // Assign object based on 1: defaults, 2: The values in the group, 3: the values of the source.
          mergeWith(
            draft.sources.byId[targetId],
            defaultS,
            synchronizationGroup,
            draft.sources.byId[targetId],
            // eslint-disable-next-line id-length, @typescript-eslint/no-unsafe-return
            (a, b) => (b === null ? a : undefined),
          );
        }
      }
    },
    syncGroupRemoveTarget: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupRemoveTargetPayload>,
    ) => {
      const { groupId, targetId } = action.payload;
      if (!draft.groups.byId[groupId]) {
        return;
      }
      const synchronizationGroup = draft.groups.byId[groupId];
      if (!synchronizationGroup.targets.allIds.includes(targetId)) {
        const sourcePanelId = getFeatureSourcePanelId(synchronizationGroup);
        if (
          synchronizationGroup.type === SYNCGROUPS_TYPE_SETFEATURES &&
          sourcePanelId
        ) {
          const sourceTargets = draft.sources.byId[sourcePanelId]?.targets;
          if (sourceTargets) {
            delete sourceTargets.byId[targetId];
            removeInPlace(sourceTargets.allIds, (_id) => _id === targetId);
          }
        }
        return;
      }
      delete draft.groups.byId[groupId].targets.byId[targetId];
      removeInPlace(
        draft.groups.byId[groupId].targets.allIds,
        (_id) => _id === targetId,
      );
      const sourcePanelId = getFeatureSourcePanelId(synchronizationGroup);
      if (
        synchronizationGroup.type === SYNCGROUPS_TYPE_SETFEATURES &&
        sourcePanelId
      ) {
        const sourceTargets = draft.sources.byId[sourcePanelId]?.targets;
        if (sourceTargets) {
          delete sourceTargets.byId[targetId];
          removeInPlace(sourceTargets.allIds, (_id) => _id === targetId);
        }
      }
    },
    syncGroupLinkTarget: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupLinkTargetPayload>,
    ) => {
      const { groupId, targetId, linked } = action.payload;
      const synchronizationGroup = draft.groups.byId[groupId];
      if (!synchronizationGroup?.targets.byId[targetId]) {
        const sourcePanelId = getFeatureSourcePanelId(synchronizationGroup);
        if (
          synchronizationGroup?.type === SYNCGROUPS_TYPE_SETFEATURES &&
          sourcePanelId
        ) {
          const sourceTargets = draft.sources.byId[sourcePanelId]?.targets;
          if (sourceTargets?.byId[targetId]) {
            sourceTargets.byId[targetId].linked = linked;
          }
        }
        return;
      }
      synchronizationGroup.targets.byId[targetId].linked = linked;
      const sourcePanelId = getFeatureSourcePanelId(synchronizationGroup);
      if (
        synchronizationGroup.type === SYNCGROUPS_TYPE_SETFEATURES &&
        sourcePanelId
      ) {
        const sourceTargets = draft.sources.byId[sourcePanelId]?.targets;
        if (sourceTargets?.byId[targetId]) {
          sourceTargets.byId[targetId].linked = linked;
        }
      }
    },
    syncGroupAddGroup: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupAddGroupPayload>,
    ) => {
      const { groupId, type, title, sourcePanelId } = action.payload;
      if (draft.groups.byId[groupId]) {
        if (
          sourcePanelId &&
          type === SYNCGROUPS_TYPE_SETFEATURES &&
          !draft.groups.byId[groupId].sourcePanelId
        ) {
          draft.groups.byId[groupId].sourcePanelId = sourcePanelId;
        }
        return;
      }
      draft.groups.byId[groupId] = {
        title,
        type,
        sourcePanelId,
        payloadByType: {
          SYNCGROUPS_TYPE_SETTIME: null!,
          SYNCGROUPS_TYPE_SETBBOX: null!,
          SYNCGROUPS_TYPE_SETFEATURES: null!,
        },
        targets: {
          allIds: [],
          byId: {},
        },
      };
      draft.groups.allIds.push(groupId);
    },
    syncGroupRemoveGroup: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupRemoveGroupPayload>,
    ) => {
      const { groupId } = action.payload;
      if (!draft.groups.byId[groupId]) {
        return;
      }
      delete draft.groups.byId[groupId];
      removeInPlace(draft.groups.allIds, (_groupId) => _groupId === groupId);
    },
    syncGroupClear: () => {
      return initialState;
    },
    syncGroupSetViewState: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupSetViewStatePayload>,
    ) => {
      const { viewState } = action.payload;
      draft.viewState = viewState;
    },

    syncGroupToggleIsTimeScrollingEnabled: (
      draft: Draft<SynchronizationGroupState>,
      action: PayloadAction<SyncGroupToggleIsTimeScrollingEnabled>,
    ) => {
      draft.isTimeScrollingEnabled = action.payload.isTimeScrollingEnabled;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setTimeSync, (draft, action) => {
        return void setBboxOrTimeSync(draft, action);
      })
      .addCase(setBboxSync, (draft, action) => {
        return void setBboxOrTimeSync(draft, action);
      });
  },
});

const getPayLoadKey = (type: string): string | null => {
  switch (type) {
    case setTimeSync.type:
      return SYNCGROUPS_TYPE_SETTIME;
    case setBboxSync.type:
      return SYNCGROUPS_TYPE_SETBBOX;
    default:
      return null;
  }
};

const mergePayloadInToState = (
  draftSource: WritableDraft<SynchronizationSource | SynchronizationGroup>,
  payloadKey: string,
  payload: SetTimePayload | SetBboxPayload,
): void => {
  if (!draftSource) {
    // Do nothing if draftSource is undefined/null
    return;
  }
  if (!draftSource.payloadByType) {
    draftSource.payloadByType = {};
    draftSource.payloadByType[payloadKey] = payload;
    return;
  }
  draftSource.payloadByType[payloadKey] = merge(
    draftSource.payloadByType[payloadKey],
    payload,
  );
};

const setBboxOrTimeSync = (
  draft: Draft<SynchronizationGroupState>,
  action: PayloadAction<SetBboxSyncActionPayload | SetTimeSyncActionPayload>,
): void => {
  const { targets, source, groups } = action.payload;

  if (source) {
    const payloadKey = getPayLoadKey(action.type);
    if (!source.payload || !payloadKey) {
      console.warn('Wrong sync action: ', action);
      return;
    }
    mergePayloadInToState(
      draft.sources.byId[source.payload.sourceId],
      payloadKey,
      source.payload,
    );
    /* Update all targets */
    targets.forEach((target) => {
      mergePayloadInToState(
        draft.sources.byId[target.targetId],
        payloadKey,
        source.payload,
      );
    });
    /* Set the value in the group */
    groups.forEach((group: string) => {
      mergePayloadInToState(
        draft.groups.byId[group],
        payloadKey,
        source.payload,
      );
    });
  }
};

const ensureFeatureGroupForPanel = (
  draft: Draft<SynchronizationGroupState>,
  panelId: string,
): string => {
  const groupId = getFeatureGroupId(panelId);
  if (!draft.groups.byId[groupId]) {
    draft.groups.byId[groupId] = {
      title: 'Features',
      type: SYNCGROUPS_TYPE_SETFEATURES,
      sourcePanelId: panelId,
      payloadByType: {
        SYNCGROUPS_TYPE_SETTIME: null!,
        SYNCGROUPS_TYPE_SETBBOX: null!,
        SYNCGROUPS_TYPE_SETFEATURES: null!,
      },
      targets: {
        allIds: [],
        byId: {},
      },
    };
    draft.groups.allIds.push(groupId);
  } else if (!draft.groups.byId[groupId].sourcePanelId) {
    draft.groups.byId[groupId].sourcePanelId = panelId;
  }
  return groupId;
};

const getFeatureSourcePanelId = (
  synchronizationGroup:
    | Draft<SynchronizationGroup>
    | SynchronizationGroup
    | undefined,
): string | undefined => synchronizationGroup?.sourcePanelId;

const ensureFeatureSourceTargetsForPanel = (
  draft: Draft<SynchronizationGroupState>,
  panelId: string,
): void => {
  if (!draft.sources.byId[panelId]) {
    draft.sources.byId[panelId] = {
      types: [SYNCGROUPS_TYPE_SETFEATURES],
      payloadByType: {},
      targets: {
        byId: {},
        allIds: [],
      },
    };
    if (!draft.sources.allIds.includes(panelId)) {
      draft.sources.allIds.push(panelId);
    }
  } else if (
    !draft.sources.byId[panelId].types.includes(SYNCGROUPS_TYPE_SETFEATURES)
  ) {
    draft.sources.byId[panelId].types.push(SYNCGROUPS_TYPE_SETFEATURES);
  }
  if (!draft.sources.byId[panelId].targets) {
    draft.sources.byId[panelId].targets = {
      byId: {},
      allIds: [],
    };
  }
};

const createTargets = (
  targetIds: string[],
): {
  byId: Record<string, { linked: boolean }>;
  allIds: string[];
} => ({
  byId: targetIds.reduce<Record<string, { linked: boolean }>>(
    (acc, targetId) => ({
      ...acc,
      [targetId]: { linked: true },
    }),
    {},
  ),
  allIds: targetIds,
});

export const {
  syncGroupAddGroup,
  syncGroupAddSource,
  syncGroupAddTarget,
  syncGroupClear,
  syncGroupLinkTarget,
  syncGroupRemoveGroup,
  syncGroupRemoveSource,
  syncGroupRemoveTarget,
  syncGroupSetViewState,
} = slice.actions;

export const { reducer: syncGroupsReducer, actions: syncGroupsActions } = slice;
